/* eslint-disable web/require-page-view-tracking -- page-view tracking is delegated to
   <MarketingCrossDomainLogin>, which calls the injected analytics.trackPageView('/login', …)
   on mount (covered by @puffin-core/login-shell's MarketingCrossDomainLogin test). */
/**
 * @fileoverview MarketingLoginPage — cross-domain login on www.patrolpuffin.com.
 *
 * MKT-LOGIN-XDOMAIN-001 (2026-06-29): a thin patrol-web wrapper of
 * `@puffin-core/login-shell` `<MarketingCrossDomainLogin>` (the ~355-line
 * orchestration promoted from pos-web). This page injects patrol's brand + SEO +
 * analytics + captcha + the canonical subdomain-persistence + patrol's
 * marketing-login auth hook.
 *
 * @module portals/marketing-website/pages/MarketingLoginPage
 */

import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { MarketingCrossDomainLogin, createSubdomainPersistence } from '@puffin-core/login-shell';
import { CaptchaField } from '@puffin-core/captcha-ui';
import { MarketingSeo, signupUrlFor, useAnalytics, webPage } from '@puffin-core/marketing-shell';
import { getBrand } from '@puffin-core/brand-assets';
import useMarketingLoginAuth from '../hooks/useMarketingLoginAuth';

const APP_KEY = 'patrol';
const LOGIN_CANONICAL = 'https://patrolpuffin.com/login';

// Canonical localStorage-backed remembered-subdomain store, namespaced to
// patrol-web's `pt-auth` auth namespace. Built once at module scope (the store
// is stateless beyond the storage key).
const subdomainPersistence = createSubdomainPersistence({ storageKey: 'pt-auth_subdomain' });

/**
 * patrol-web's cross-domain marketing-login page (www.patrolpuffin.com/login).
 * Thin wrapper of `<MarketingCrossDomainLogin>`; page-view tracking is delegated
 * to that component (hence the `web/require-page-view-tracking` disable above).
 *
 * @returns {React.JSX.Element} The cross-domain login surface.
 */
function MarketingLoginPage() {
  const { t } = useTranslation();
  const analytics = useAnalytics();
  const captchaRef = useRef(null);

  const brand = getBrand(APP_KEY);
  const ogImageUrl = `https://patrolpuffin.com${brand.assets.ogCard}`;

  const schemaData = webPage({
    appKey: APP_KEY,
    brand,
    // Canonical URL for the JSON-LD WebPage node (login is client-only, never
    // prerendered — so a window-derived href would only ever be this anyway).
    url: LOGIN_CANONICAL,
    title: t('login.seo.schemaName'),
    description: t('login.seo.schemaDescription'),
  });

  const seoSlot = (
    <MarketingSeo
      title={t('login.pageTitle')}
      description={t('login.pageDescription')}
      canonical={LOGIN_CANONICAL}
      ogImage={ogImageUrl}
      ogType="website"
      jsonLd={[schemaData]}
    />
  );

  return (
    <MarketingCrossDomainLogin
      useAuth={useMarketingLoginAuth}
      t={t}
      appName="PatrolPuffin"
      appKey="patrol"
      baseDomain=".patrolpuffin.com"
      analytics={analytics}
      seoSlot={seoSlot}
      subdomainPersistence={subdomainPersistence}
      passwordResetCaptchaSlot={<CaptchaField ref={captchaRef} />}
      getCaptchaToken={() => captchaRef.current?.getToken() ?? null}
      startTrialHref={signupUrlFor({ app: 'patrol' })}
    />
  );
}

export default MarketingLoginPage;
